import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, X, Phone } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEOHead from '../components/SEOHead';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { usePublicT } from '../i18n/usePublicT';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PricingPage() {
  const pt = usePublicT();
  const [showSignup, setShowSignup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [signupData, setSignupData] = useState({ company_name: '', email: '', password: '', phone: '' });
  const [loading, setLoading] = useState(false);

  const PLANS = [
    {
      id: 'starter', nameKey: 'price.starter', price: 99, descKey: 'price.starterDesc',
      featureKeys: ['planFeat.up100', 'planFeat.3members', 'planFeat.trilingual', 'planFeat.stripePaypal', 'planFeat.packageTracking', 'planFeat.clientPortal', 'planFeat.basicReports', 'planFeat.scanner', 'planFeat.csvImportShort'],
      ctaKey: 'price.getStarted', popular: false,
    },
    {
      id: 'pro', nameKey: 'price.professional', price: 249, descKey: 'price.professionalDesc',
      featureKeys: ['planFeat.up500', 'planFeat.10members', 'planFeat.everythingStarter', 'planFeat.advReports', 'planFeat.excelPdfExport', 'planFeat.emailNotif', 'planFeat.whatsappNotif', 'planFeat.pushNotifPortal', 'planFeat.customDomain', 'planFeat.batchStatusUpd'],
      ctaKey: 'price.startPro', popular: true,
    },
    {
      id: 'enterprise', nameKey: 'price.enterprise', price: null, descKey: 'price.enterpriseDesc',
      featureKeys: ['planFeat.unlimited', 'planFeat.unlimitedUsers', 'planFeat.everythingPro', 'planFeat.anyLang', 'planFeat.customBranding', 'planFeat.prioritySupport', 'planFeat.apiAccess', 'planFeat.dedicatedMgr', 'planFeat.customInteg', 'planFeat.sla'],
      ctaKey: 'price.contactUs', popular: false,
    },
  ];

  const FAQS = [
    { qKey: 'faq.q1', aKey: 'faq.a1' },
    { qKey: 'faq.q2', aKey: 'faq.a2' },
    { qKey: 'faq.q3', aKey: 'faq.a3' },
    { qKey: 'faq.q4', aKey: 'faq.a4' },
    { qKey: 'faq.q5', aKey: 'faq.a5' },
  ];

  const handleSelectPlan = (planId) => {
    if (planId === 'enterprise') {
      window.open('https://wa.me/+50688888888?text=Hi!%20I%27m%20interested%20in%20the%20Enterprise%20plan.', '_blank');
    } else {
      setSelectedPlan(planId);
      setShowSignup(true);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const regRes = await axios.post(`${API_URL}/api/companies/register`, { ...signupData, plan: selectedPlan });
      const subRes = await axios.post(`${API_URL}/api/companies/subscribe`, {
        company_id: regRes.data.company_id, plan: selectedPlan, origin_url: window.location.origin, payment_method: paymentMethod,
      });
      window.location.href = subRes.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <SEOHead
        title="Pricing — Courier CRM Plans Starting at $99/mo"
        description="Simple pricing for courier companies. Starter plan at $99/mo, Professional at $249/mo."
        path="/pricing"
        keywords="courier CRM pricing, shipping software cost, logistics SaaS pricing"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'Product', name: 'Track My Package', offers: [{ '@type': 'Offer', name: 'Starter', price: '99', priceCurrency: 'USD' }, { '@type': 'Offer', name: 'Professional', price: '249', priceCurrency: 'USD' }] }}
      />

      <PublicNav activePage="/pricing" />

      {/* Hero */}
      <section className="pt-28 pb-8 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">{pt('price.transparentTitle')}</h1>
          <p className="text-gray-400 mt-4 text-base sm:text-lg">{pt('price.noHidden')}</p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((plan, i) => (
            <motion.div key={plan.id}
              className={`relative bg-white/[0.03] border rounded-2xl p-6 flex flex-col ${plan.popular ? 'border-[#FF9900]/40 ring-1 ring-[#FF9900]/20' : 'border-white/10'}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              data-testid={`pricing-plan-${plan.id}`}
            >
              {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF9900] text-white text-xs font-bold px-3 py-1 rounded-full">{pt('price.mostPopular')}</div>}
              <h3 className="text-xl font-bold text-white">{pt(plan.nameKey)}</h3>
              <p className="text-sm text-gray-400 mt-1 mb-4">{pt(plan.descKey)}</p>
              <div className="mb-5">
                {plan.price ? (
                  <><span className="text-4xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>${plan.price}</span><span className="text-gray-500 text-sm">{pt('price.month')}</span></>
                ) : <span className="text-2xl font-bold text-white">{pt('price.custom')}</span>}
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.featureKeys.map((fk, fi) => (
                  <li key={fi} className="flex items-start gap-2 text-sm text-gray-300">
                    <Check size={14} className="text-[#FF9900] mt-0.5 flex-shrink-0" weight="bold" />
                    {pt(fk)}
                  </li>
                ))}
              </ul>
              <Button onClick={() => handleSelectPlan(plan.id)}
                className={`w-full rounded-xl py-5 text-base font-medium ${plan.popular ? 'bg-[#FF9900] hover:bg-[#FF9900]/90 text-white' : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'}`}
                data-testid={`pricing-cta-${plan.id}`}
              >
                {pt(plan.ctaKey)} <ArrowRight size={16} className="ml-2" />
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Support Add-on */}
        <motion.div initial={{ opacity: 0, x: -60 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 60 }}
          className="mt-10 max-w-3xl mx-auto">
          <div className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6 hover:border-[#0077FF]/30 transition-colors" data-testid="support-addon-card">
            <div className="w-14 h-14 rounded-2xl bg-[#0077FF]/10 flex items-center justify-center flex-shrink-0">
              <Phone size={28} className="text-[#0077FF]" weight="duotone" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-xl font-bold text-white">{pt('price.supportTitle')}</h3>
              <p className="text-sm text-gray-400 mt-1">{pt('price.supportDesc')}</p>
            </div>
            <div className="text-center flex-shrink-0">
              <div className="mb-2"><span className="text-3xl font-bold text-white">$99</span><span className="text-sm text-gray-400">{pt('price.month')}</span></div>
              <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')} className="bg-[#0077FF] hover:bg-[#0066DD] text-white rounded-xl px-6 text-sm" data-testid="support-addon-btn">
                {pt('price.addSupport')} <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 bg-[#060B15]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">{pt('faq.title')}</h2>
          {FAQS.map((faq, i) => (
            <motion.div key={i} className="mb-4 bg-white/[0.03] border border-white/10 rounded-xl p-5"
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <h3 className="font-bold text-white text-sm">{pt(faq.qKey)}</h3>
              <p className="text-sm text-gray-400 mt-1">{pt(faq.aKey)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <PublicFooter />

      {/* Signup Modal */}
      {showSignup && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowSignup(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0B1120] border border-white/10 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="signup-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl font-bold text-white">{pt('signup.title')}</h3>
                <p className="text-sm text-gray-400 mt-1">
                  {PLANS.find(p => p.id === selectedPlan)?.nameKey ? pt(PLANS.find(p => p.id === selectedPlan).nameKey) : ''} Plan - ${PLANS.find(p => p.id === selectedPlan)?.price}{pt('price.month')}
                </p>
              </div>
              <button onClick={() => setShowSignup(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleSignup} className="space-y-3">
              <Input value={signupData.company_name} onChange={e => setSignupData({...signupData, company_name: e.target.value})} placeholder={pt('signup.companyName')} className="rounded-xl h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required />
              <Input type="email" value={signupData.email} onChange={e => setSignupData({...signupData, email: e.target.value})} placeholder={pt('signup.email')} className="rounded-xl h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required />
              <Input type="password" value={signupData.password} onChange={e => setSignupData({...signupData, password: e.target.value})} placeholder={pt('signup.password')} className="rounded-xl h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required minLength={6} />
              <div className="flex gap-2 p-1 bg-white/5 rounded-xl">
                {['starter', 'pro'].map(pid => (
                  <button key={pid} type="button" onClick={() => setSelectedPlan(pid)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${selectedPlan === pid ? 'bg-[#FF9900] text-white shadow' : 'text-gray-400'}`}>
                    {pt(PLANS.find(p => p.id === pid)?.nameKey || 'price.starter')} - ${PLANS.find(p => p.id === pid)?.price}/mo
                  </button>
                ))}
              </div>
              <div className="flex gap-2 p-1 bg-white/5 rounded-xl">
                <button type="button" onClick={() => setPaymentMethod('stripe')} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${paymentMethod === 'stripe' ? 'bg-white/10 text-white' : 'text-gray-500'}`}>Stripe</button>
                <button type="button" onClick={() => setPaymentMethod('paypal')} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${paymentMethod === 'paypal' ? 'bg-white/10 text-white' : 'text-gray-500'}`}>PayPal</button>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#FF9900] hover:bg-[#FF9900]/90 text-white rounded-xl py-5 text-base font-medium disabled:opacity-60" data-testid="signup-submit-btn">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{pt('signup.continueTo')} {paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'} <ArrowRight size={18} className="ml-2" /></>}
              </Button>
              <p className="text-xs text-gray-500 text-center">{pt('signup.bySigningUp')} {paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'} {pt('signup.forPayment')}</p>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
